import {ToolCategories, type Tool} from "./ai-tools-list.ts";
import type {getFavoritedTeacherTools_aiGuideFavoritedTeacherTools as TeacherTool} from "./hooks/__graphql-types__/getFavoritedTeacherTools.ts";

export enum Platform {
    KhanAcademy = "KhanAcademy",
    Canvas = "Canvas",
}

/**
 * Compares and filters tools based on user permissions and favorites.
 *
 * @param {Array<Tool>} filteredTools - Filtered tools based on the
 * permissions a user has.
 *
 * @param {Array<TeacherTool>} favoritedTools - Tools are added to or removed
 * from this list when a user favorites or unfavorites them. This action
 * state is maintained in the backend. This list helps manage tool visibility
 * across different user states, such as language-specific tool access.
 *
 * @returns {Array<Tool>} - A list of tools that the user has both favorited
 * and permission to access. This ensures that the displayed tools are both relevant and accessible to the user.
 */
export const getFavoritedToolsWithPermission = (
    filteredTools: Array<Tool>,
    favoritedTools: Array<TeacherTool>,
): Array<Tool> => {
    // If there are no tools to filter, return the filtered tools as is
    if (filteredTools.length === 0) {
        return filteredTools;
    }
    // Create a set for quick lookup
    const favoritedToolsSet = new Set(
        favoritedTools.map((tool) => tool.configName),
    );

    // Iterate over each tool in filteredTools to know if it is favorited
    return filteredTools.map((tool) => {
        // Check if the current tool is in the set of favorited tools
        const isFavorited = favoritedToolsSet.has(tool.configName);

        // If the tool is favorited and does not already have the 'Favorites' category, update it
        if (
            isFavorited &&
            !tool.categories.includes(ToolCategories.Favorites)
        ) {
            return {
                ...tool,
                categories: [...tool.categories, ToolCategories.Favorites],
            };
        }

        // If the tool is not favorited and has the 'Favorites' category, remove it
        if (
            !isFavorited &&
            tool.categories.includes(ToolCategories.Favorites)
        ) {
            return {
                ...tool,
                categories: tool.categories.filter(
                    (category) => category !== ToolCategories.Favorites,
                ),
            };
        }

        // Return the tool as is if no updates are needed
        return tool;
    });
};

type ToolIdentifier = {
    id: string;
    configName: string;
};

/**
 * Gets the tool based on the configName.
 *
 * @param {Array<T>} toolsList - The list of tools available to the user.
 * @param {string} configName - The configName of the tool.
 *
 * @returns {T | undefined} - The tool object if found, otherwise undefined.
 */
export const getToolId = <T extends ToolIdentifier>(
    toolsList: Array<T> | undefined,
    configName: string,
): T | undefined => {
    if (!toolsList) {
        return undefined;
    }
    return toolsList.find((tool) => tool.configName === configName);
};
